import { Show } from '@/components/show'
import {
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TableHeader,
  Table,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { TableData } from '@/types/table/table-data'
import type { TableHead as TableHeadType } from '@/types/table/table-head'
import { dataToTableCell } from '@/utils/data-to-table-cell'
import { getCellTextStyle } from '@/utils/get-cell-text-style'
import { getNestedValue } from '@/utils/get-nested-value'
import clsx from 'clsx'
import React, { type JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

interface TableMemoProps<T> {
  data: TableData<T>[]
  heads: TableHeadType[]
  isLoading?: boolean
  isFetching?: boolean
}

export const TableMemo = <T,>({
  data,
  heads,
  isLoading,
  isFetching,
}: TableMemoProps<T>) => {
  const [queryParams] = useSearchParams()
  const navigate = useNavigate()

  function handleOpenRow(row: TableData<T>) {
    if (row.redirectURL) {
      navigate(row.redirectURL)
    }
  }

  function generateSkeletonLoading() {
    const rows: JSX.Element[] = []
    const limit = queryParams.get('limit')
    const items = Number(limit) || 10

    for (let i = 0; i < items; i++) {
      rows.push(
        <TableRow
          className="shadow-table animate-pulse"
          key={`skeleton-row-${i}`}
        >
          {heads.map(
            (_item, index) =>
              !_item.hidden && (
                <TableCell key={`skeleton-${index}`} className="px-6 max-w-64">
                  <div className="text-transparent bg-ring/20 rounded-sm">
                    0
                  </div>
                </TableCell>
              ),
          )}
        </TableRow>,
      )
    }

    return rows
  }

  return (
    <Table className="px-1">
      <TableHeader>
        <TableRow>
          {heads.map(
            (head) =>
              !head.hidden && (
                <TableHead className="px-6" key={String(head.key)}>
                  {head.label}
                </TableHead>
              ),
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        <>
          <Show when={isLoading || isFetching}>
            {generateSkeletonLoading()}
          </Show>
          <Show when={!isLoading}>
            {data.map((row, index) => (
              <TableRow
                key={index}
                className={clsx('shadow-table', {
                  'cursor-pointer': row.redirectURL,
                })}
                onClick={() => handleOpenRow(row)}
              >
                {heads.map((head) => {
                  const value = getNestedValue(row.data, head.key)
                  const color = getCellTextStyle(head, value)
                  const label = dataToTableCell(head, value)
                  return (
                    !head.hidden && (
                      <TableCell
                        key={String(head.key)}
                        className="px-6 max-w-64 truncate"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              className={clsx(
                                `${color}`,
                                'overflow-hidden text-ellipsis whitespace-nowrap',
                              )}
                            >
                              {label}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{label}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                    )
                  )
                })}
              </TableRow>
            ))}
          </Show>
        </>
      </TableBody>
    </Table>
  )
}

export const MemoizedTable = React.memo(TableMemo) as typeof TableMemo
